import { Component, Input, OnInit } from '@angular/core';
import { BaseComponent } from '@shared-features/components/base-component/base.component';
import { DateRangeType, FormAction } from '@shared-features/enums/shared-enums.enum';
import { TabGroup } from '@shared-features/enums/tab.enum';
import { User } from '@features/users/models/user.model';
import { FormControl, FormGroup } from '@angular/forms';
import { FilterService } from '../../../filter/services/filter.service';
import { takeUntil } from 'rxjs/operators';
import { forkJoin, Observable, of } from 'rxjs';
import { ConfigConstant } from '@shared/constants';
import { ActivatedRoute } from '@angular/router';
import { FunctionUtil } from '@shared/utils/function.util';
import { SharedAPI, UserApi } from '@shared-features/services';
import { Customer } from '@features/customers/models/customer.model';

@Component({
  selector: 'app-transportation-payments-shipments-filter',
  templateUrl: './transportation-payments-shipments-filter.component.html',
  styleUrls: ['./transportation-payments-shipments-filter.component.scss'],
})
export class TransportationPaymentsShipmentsFilterComponent extends BaseComponent implements OnInit {
  @Input() action: Observable<void>;
  @Input() tabGroup: TabGroup;

  form: FormGroup;
  params: any = null;
  GlobalConstants = ConfigConstant;
  dateRangeType = DateRangeType;
  users: User[] = [];
  customers: Customer[] = [];
  maxDate: Date = new Date();
  groups = TabGroup;

  constructor(
    private filterService: FilterService,

    private activatedRoute: ActivatedRoute,
    private userApi: UserApi,
    private sharedAPI: SharedAPI
  ) {
    super();
  }

  ngOnInit(): void {
    this.load(
      forkJoin([this.tabGroup === TabGroup.PaymentDetails ? this.sharedAPI.getAllCustomers() : of(null), this.userApi.getAllUsers()])
    ).subscribe(([customers, users]) => {
      this.customers = customers;
      this.users = users;
      this.initForm();
    });
    this.subscribeToQueryParams();
    this.subscribeToFilterAction();
  }

  subscribeToQueryParams(): void {
    this.activatedRoute.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      if (params && Object.keys(params).length !== 0) {
        this.params = params;
      }
    });
  }

  getFilterValuesBasedOnURLParams() {
    return {
      customerId: this.params.customerId === ConfigConstant.ALL ? ConfigConstant.ALL : Number(this.params.customerId),
      dateRange: this.params.dateRange === DateRangeType.ALL_TIME ? DateRangeType.ALL_TIME : String(this.params.dateRange),
      startFrom: this.params.startFrom ? String(this.params.startFrom) : null,
      endAt: this.params.endAt ? String(this.params.endAt) : null,
      creatorId: this.params.creatorId === ConfigConstant.ALL ? ConfigConstant.ALL : Number(this.params.creatorId),
    };
  }

  initForm() {
    if (this.params && Object.keys(this.params).length !== 0) {
      const filterValues = this.getFilterValuesBasedOnURLParams();
      this.form = new FormGroup({
        customerId: new FormControl(filterValues.customerId),
        creatorId: new FormControl(filterValues.creatorId),
        dateRange: new FormControl(filterValues.dateRange ? filterValues.dateRange : DateRangeType.ALL_TIME),
        startFrom: new FormControl(filterValues?.startFrom),
        endAt: new FormControl(filterValues?.endAt),
        creatorSearchTerm: new FormControl(''),
        customerSearchTerm: new FormControl(''),
      });
    } else {
      this.form = new FormGroup({
        customerId: new FormControl(ConfigConstant.ALL),
        creatorId: new FormControl(ConfigConstant.ALL),
        dateRange: new FormControl(DateRangeType.ALL_TIME),
        startFrom: new FormControl(null),
        endAt: new FormControl(null),
        creatorSearchTerm: new FormControl(''),
        customerSearchTerm: new FormControl(''),
      });
    }

    this.form
      .get('dateRange')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if (value !== DateRangeType.CUSTOM) {
          this.form.controls['startFrom'].setValue(null);
          this.form.controls['endAt'].setValue(null);
        }
      });
  }

  get filteredCreators() {
    return this.users.filter((user) =>
      user.dropDownDisplayName.toLowerCase().includes(this.form.controls.creatorSearchTerm.value?.toLowerCase())
    );
  }

  get filteredCustomers() {
    return this.customers.filter((user) =>
      user.dropDownDisplayName.toLowerCase().includes(this.form.controls.customerSearchTerm.value?.toLowerCase())
    );
  }

  subscribeToFilterAction() {
    this.action.pipe(takeUntil(this.destroy$)).subscribe((action: any) => {
      switch (action) {
        case FormAction.Reset:
          this.resetForm();
          break;

        case FormAction.Cancel:
          this.cancelChanges();
          break;

        case FormAction.Submit:
          this.submitForm();
          break;
      }
    });
  }

  resetForm() {
    this.form.setValue({
      customerId: ConfigConstant.ALL,
      creatorId: ConfigConstant.ALL,
      dateRange: DateRangeType.ALL_TIME,
      startFrom: null,
      endAt: null,
      creatorSearchTerm: '',
      customerSearchTerm: '',
    });
    this.submitForm();
  }

  cancelChanges() {
    if (this.params && Object.keys(this.params).length !== 0) {
      const filterValues = this.getFilterValuesBasedOnURLParams();
      this.form.setValue({
        customerId: filterValues.customerId,
        creatorId: filterValues.creatorId,
        dateRange: filterValues.dateRange,
        startFrom: filterValues.startFrom,
        endAt: filterValues.endAt,
        creatorSearchTerm: '',
        customerSearchTerm: '',
      });
    } else {
      this.resetForm();
    }
  }

  submitForm() {
    const { creatorSearchTerm, customerSearchTerm, ...submittedFilterValues } = this.form.value;
    if (
      this.form.controls.startFrom.value &&
      this.form.controls.endAt.value &&
      this.form.controls.dateRange.value === DateRangeType.CUSTOM
    ) {
      submittedFilterValues.startFrom = FunctionUtil.convertDate(this.form.controls.startFrom.value);
      submittedFilterValues.endAt = FunctionUtil.convertDate(this.form.controls.endAt.value);
    } else {
      submittedFilterValues.startFrom = null;
      submittedFilterValues.endAt = null;
    }

    submittedFilterValues.tabGroup = this.tabGroup;
    this.filterService.navigateToTargetPageWithFilter(submittedFilterValues);
  }
}
